import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Bookmark, Check, CircleCheck, CircleX, Cloud, CloudOff, Flag, LoaderCircle, LogOut, Timer,
} from 'lucide-react';
import { examService } from '../services/api';
import { CourseContext } from '../contexts/CourseContext';
import { AppShell, Badge, Brand, Button, Card, EmptyState, Loader, Modal, ProgressBar } from '../components/ui';
import { extraNote } from '../utils/questionNote';
import './ExamPage.css';

const AUTOSAVE_DELAY = 1200;   // após responder, marcar ou navegar
const AUTOSAVE_PERIOD = 15000; // para guardar o tempo restante

function ExamPage() {
  const { examId, courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useContext(CourseContext);

  const buildPath = (path) => (courseSlug ? `/curso/${courseSlug}${path}` : path);

  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'exit' | 'finish'
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [finishing, setFinishing] = useState(false);

  // Espelho do estado atual, lido pelo salvamento e pela finalização (evita closures antigas)
  const live = useRef({ answers: {}, flagged: new Set(), currentIndex: 0, remaining: 0 });
  live.current = { answers, flagged: flaggedQuestions, currentIndex, remaining: remainingSeconds };
  const finishingRef = useRef(false);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (courseSlug) selectCourse(courseSlug);
  }, [courseSlug, selectCourse]);

  const progressPayload = useCallback(() => ({
    answers: live.current.answers,
    flagged: Array.from(live.current.flagged),
    currentIndex: live.current.currentIndex,
    remainingSeconds: live.current.remaining,
  }), []);

  // ── Carregar e retomar ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [examData, state] = await Promise.all([examService.getExamById(examId), examService.getState(examId)]);
        if (cancelled) return;
        if (state.completed) {
          navigate(buildPath(`/results/${examId}`), { replace: true });
          return;
        }
        const qs = (examData.examQuestions || [])
          .sort((a, b) => a.questionOrder - b.questionOrder)
          .map(eq => eq.question);

        // Respostas salvas chegam com o id da questão como chave (texto no JSON)
        const restored = {};
        Object.entries(state.answers || {}).forEach(([qid, letters]) => { restored[Number(qid)] = letters; });

        setExam(examData);
        setQuestions(qs);
        setAnswers(restored);
        setFlaggedQuestions(new Set(state.flagged || []));
        setCurrentIndex(Math.min(state.currentIndex || 0, Math.max(qs.length - 1, 0)));
        setRemainingSeconds(state.remainingSeconds ?? (examData.durationMinutes || 60) * 60);
        if (examData.correctionMode === 'immediate') {
          // Na correção imediata, as questões já respondidas voltam com o gabarito à mostra
          const fb = {};
          qs.forEach(q => { if (restored[q.id]?.length && q.questionType !== 'multiple_choice') fb[q.id] = true; });
          setShowFeedback(fb);
        }
        restoredRef.current = true;
        setLoading(false);
        examService.startExam(examId).catch(() => {});
      } catch (error) {
        console.error('Error loading exam:', error);
        if (!cancelled) {
          setLoadError(true);
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  // ── Salvamento automático ────────────────────────────────────────
  const save = useCallback(async () => {
    if (finishingRef.current || !restoredRef.current) return;
    setSaveState('saving');
    try {
      await examService.saveProgress(examId, progressPayload());
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [examId, progressPayload]);

  useEffect(() => {
    if (loading || !restoredRef.current) return undefined;
    const t = setTimeout(save, AUTOSAVE_DELAY);
    return () => clearTimeout(t);
  }, [answers, flaggedQuestions, currentIndex, loading, save]);

  useEffect(() => {
    if (loading) return undefined;
    const id = setInterval(save, AUTOSAVE_PERIOD);
    return () => clearInterval(id);
  }, [loading, save]);

  // Fechar a aba ou sair do app: último salvamento, com keepalive para não ser cortado
  useEffect(() => {
    const onHide = () => {
      if (finishingRef.current || !restoredRef.current) return;
      fetch(`/api/exams/${examId}/progress`, {
        method: 'PUT',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(progressPayload()),
      }).catch(() => {});
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [examId, progressPayload]);

  // ── Finalização (manual ou por tempo esgotado) ───────────────────
  const handleFinish = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    setFinishing(true);
    setConfirmAction(null);
    try {
      await examService.completeExam(examId, progressPayload());
      // "fresh": o resultado comemora (confete e modais) só logo após finalizar
      navigate(buildPath(`/results/${examId}`), { replace: true, state: { fresh: true } });
    } catch (error) {
      console.error('Error finishing exam:', error);
      finishingRef.current = false;
      setFinishing(false);
      setSaveState('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, progressPayload]);

  // O cronômetro só conta o tempo; quem finaliza é o efeito abaixo, que lê o estado atual
  useEffect(() => {
    if (!exam || loading) return undefined;
    const id = setInterval(() => setRemainingSeconds(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(id);
  }, [exam, loading]);

  useEffect(() => {
    if (exam && !loading && remainingSeconds === 0) handleFinish();
  }, [remainingSeconds, exam, loading, handleFinish]);

  const handleAnswer = (optionLetter) => {
    const currentQuestion = questions[currentIndex];
    if (exam.correctionMode === 'immediate' && showFeedback[currentQuestion.id]) return;

    setAnswers(prev => {
      const current = prev[currentQuestion.id] || [];
      if (currentQuestion.questionType === 'multiple_choice') {
        const next = current.includes(optionLetter) ? current.filter(a => a !== optionLetter) : [...current, optionLetter];
        return { ...prev, [currentQuestion.id]: next };
      }
      return { ...prev, [currentQuestion.id]: [optionLetter] };
    });
    if (currentQuestion.questionType !== 'multiple_choice' && exam.correctionMode === 'immediate') {
      setShowFeedback(prev => ({ ...prev, [currentQuestion.id]: true }));
    }
  };

  const confirmAnswer = () => {
    const currentQuestion = questions[currentIndex];
    setShowFeedback(prev => ({ ...prev, [currentQuestion.id]: true }));
  };

  const handleFinishEarly = () => handleFinish();

  // Sair não perde nada: salva e volta; o simulado fica em "Simulados" para retomar
  const handleExit = async () => {
    setConfirmAction(null);
    await save();
    navigate(buildPath('/simulados'));
  };

  const toggleFlag = () => {
    const questionId = questions[currentIndex].id;
    setFlaggedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  const formatTime = (seconds) => {
    const clamped = Math.max(0, seconds);
    const hours = Math.floor(clamped / 3600);
    const minutes = Math.floor((clamped % 3600) / 60);
    const secs = clamped % 60;
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  };

  if (loading) {
    return (
      <AppShell section="Simulado" courseName={currentCourse?.name}>
        <Loader label="Carregando simulado..." />
      </AppShell>
    );
  }

  if (loadError || !exam || questions.length === 0) {
    return (
      <AppShell section="Simulado" courseName={currentCourse?.name}>
        <EmptyState
          file="simulado.xml"
          title="Não foi possível carregar o simulado"
          description="Ele pode não existir mais ou o backend está fora do ar."
          action={<Button icon={ArrowLeft} onClick={() => navigate(buildPath('/estudo'))}>Voltar ao estudo</Button>}
        />
      </AppShell>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = questions.filter(q => (answers[q.id] || []).length > 0).length;
  const isFlagged = flaggedQuestions.has(currentQuestion.id);
  const isMultiple = currentQuestion.questionType === 'multiple_choice';
  const feedbackMode = exam.correctionMode === 'immediate' && showFeedback[currentQuestion.id];
  const lowTime = remainingSeconds <= 300;
  const isLast = currentIndex === questions.length - 1;
  const canAdvance = exam.correctionMode !== 'immediate' || showFeedback[currentQuestion.id] || !isMultiple;

  const renderFeedback = () => {
    const userAnswer = answers[currentQuestion.id] || [];
    const correctAnswers = currentQuestion.options.filter(opt => opt.isCorrect).map(opt => opt.optionLetter);
    const isCorrect = JSON.stringify([...userAnswer].sort()) === JSON.stringify([...correctAnswers].sort());
    return (
      <div className={`exam-feedback ${isCorrect ? 'exam-feedback--ok' : 'exam-feedback--bad'}`}>
        <div className="exam-feedback__head">
          {isCorrect ? <CircleCheck size={20} aria-hidden="true" /> : <CircleX size={20} aria-hidden="true" />}
          <strong>{isCorrect ? 'Resposta correta!' : 'Resposta incorreta'}</strong>
          {!isCorrect && <span>Correta: {correctAnswers.join(', ')}</span>}
        </div>
        {currentQuestion.explanation && currentQuestion.explanation.trim() && (
          <p className="exam-feedback__text">{currentQuestion.explanation}</p>
        )}
      </div>
    );
  };

  return (
    <div className="exam">
      <header className="exam-bar">
        <div className="exam-bar__inner">
          <Brand size="sm" className="ss-hide-mobile" />
          <div className="exam-bar__info">
            <span className="exam-bar__title">{exam.title}</span>
            <span className="exam-bar__count">Questão {currentIndex + 1} de {questions.length} · {answeredCount} respondidas</span>
          </div>
          <div className={`exam-timer ${lowTime ? 'exam-timer--low' : ''}`} role="timer" aria-live="off">
            <Timer size={18} aria-hidden="true" />
            {formatTime(remainingSeconds)}
          </div>
          <span className={`exam-save exam-save--${saveState}`} role="status" aria-live="polite">
            {saveState === 'saving' && <><LoaderCircle size={14} className="exam-save__spin" aria-hidden="true" /> Salvando</>}
            {saveState === 'saved' && <><Cloud size={14} aria-hidden="true" /> Salvo</>}
            {saveState === 'error' && <><CloudOff size={14} aria-hidden="true" /> Sem conexão</>}
          </span>
          <div className="exam-bar__actions">
            <Button variant="ghost" size="sm" icon={LogOut} onClick={() => setConfirmAction('exit')}>
              <span className="ss-hide-mobile">Pausar</span>
            </Button>
            <Button variant="accent" size="sm" icon={Flag} onClick={() => setConfirmAction('finish')}>
              Finalizar
            </Button>
          </div>
        </div>
        <ProgressBar value={progress} label="Progresso do simulado" className="exam-bar__progress" />
      </header>

      <main className="exam-main">
        <section className="exam-q">
          <Card padding="lg">
            <div className="exam-q__head">
              <span className="exam-q__num">{String(currentIndex + 1).padStart(2, '0')}</span>
              <Badge tone="navy">{currentQuestion.category?.name || 'Geral'}</Badge>
              {isMultiple && <Badge tone="warning">Múltipla escolha</Badge>}
              <button
                type="button"
                className={`exam-flag ${isFlagged ? 'is-on' : ''}`}
                onClick={toggleFlag}
                aria-pressed={isFlagged}
                title={isFlagged ? 'Remover marcação' : 'Marcar para revisar depois'}
              >
                <Bookmark size={16} aria-hidden="true" />
                {isFlagged ? 'Marcada' : 'Revisar depois'}
              </button>
            </div>

            <h1 className="exam-q__text">{currentQuestion.questionText}</h1>
            {isMultiple && !feedbackMode && <p className="exam-q__hint">Selecione todas as alternativas corretas.</p>}

            <div className="exam-options" role={isMultiple ? 'group' : 'radiogroup'}>
              {currentQuestion.options.map(option => {
                const isSelected = answers[currentQuestion.id]?.includes(option.optionLetter);
                let state = '';
                if (feedbackMode) {
                  if (option.isCorrect) state = 'is-correct';
                  else if (isSelected) state = 'is-wrong';
                }
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`exam-option ${isSelected ? 'is-selected' : ''} ${state}`}
                    onClick={() => handleAnswer(option.optionLetter)}
                    role={isMultiple ? 'checkbox' : 'radio'}
                    aria-checked={!!isSelected}
                  >
                    <span className="exam-option__letter">{option.optionLetter}</span>
                    <span className="exam-option__text">{option.optionText}</span>
                    {state === 'is-correct' && <CircleCheck size={20} className="exam-option__icon" aria-hidden="true" />}
                    {state === 'is-wrong' && <CircleX size={20} className="exam-option__icon" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>

            {feedbackMode && renderFeedback()}
            {/* A nota costuma repetir a explicação, então só aparece depois da resposta */}
            {feedbackMode && extraNote(currentQuestion) && (
              <p className="exam-q__note exam-q__note--after"><strong>Nota:</strong> {extraNote(currentQuestion)}</p>
            )}
          </Card>

          <div className="exam-nav">
            <Button
              variant="outline"
              icon={ArrowLeft}
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
            >
              Anterior
            </Button>
            <div className="exam-nav__right">
              {exam.correctionMode === 'immediate' && isMultiple && !showFeedback[currentQuestion.id] && (
                <Button
                  variant="outline"
                  icon={Check}
                  onClick={confirmAnswer}
                  disabled={!answers[currentQuestion.id] || answers[currentQuestion.id].length === 0}
                >
                  Confirmar resposta
                </Button>
              )}
              {canAdvance && (
                isLast ? (
                  <Button variant="accent" icon={Flag} onClick={() => setConfirmAction('finish')} disabled={finishing}>Finalizar simulado</Button>
                ) : (
                  <Button iconRight={ArrowRight} onClick={() => setCurrentIndex(currentIndex + 1)}>Próxima</Button>
                )
              )}
            </div>
          </div>
        </section>

        <aside className="exam-map">
          <Card padding="md">
            <div className="exam-map__head">
              <span>Questões</span>
              <span className="exam-map__count">{answeredCount}/{questions.length}</span>
            </div>
            <div className="exam-map__grid">
              {questions.map((q, i) => {
                const answered = (answers[q.id] || []).length > 0;
                const flagged = flaggedQuestions.has(q.id);
                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`exam-map__cell ${i === currentIndex ? 'is-current' : ''} ${answered ? 'is-answered' : ''} ${flagged ? 'is-flagged' : ''}`}
                    onClick={() => setCurrentIndex(i)}
                    aria-label={`Questão ${i + 1}${answered ? ', respondida' : ''}${flagged ? ', marcada para revisar' : ''}`}
                    aria-current={i === currentIndex ? 'step' : undefined}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <ul className="exam-map__legend">
              <li><span className="exam-map__dot exam-map__dot--answered" /> Respondida</li>
              <li><span className="exam-map__dot exam-map__dot--flagged" /> Revisar</li>
              <li><span className="exam-map__dot" /> Pendente</li>
            </ul>
          </Card>
        </aside>
      </main>

      <Modal
        open={confirmAction === 'exit'}
        onClose={() => setConfirmAction(null)}
        title="Pausar o simulado?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Continuar respondendo</Button>
            <Button icon={LogOut} onClick={handleExit}>Salvar e sair</Button>
          </>
        }
      >
        <p className="exam-modal-text">
          Suas respostas e o tempo restante ficam salvos. Para continuar depois, abra <strong>Simulados</strong> e clique em <strong>Retomar</strong>.
        </p>
      </Modal>

      <Modal
        open={confirmAction === 'finish'}
        onClose={() => setConfirmAction(null)}
        title="Finalizar agora?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Voltar às questões</Button>
            <Button variant="accent" icon={Flag} onClick={handleFinishEarly} disabled={finishing}>
              {finishing ? 'Corrigindo...' : 'Finalizar'}
            </Button>
          </>
        }
      >
        <p className="exam-modal-text">
          Você respondeu <strong>{answeredCount} de {questions.length}</strong> questões.
          {answeredCount < questions.length && ' As não respondidas serão consideradas incorretas.'}
          {flaggedQuestions.size === 1 && ' Há 1 questão marcada para revisar.'}
          {flaggedQuestions.size > 1 && ` Há ${flaggedQuestions.size} questões marcadas para revisar.`}
        </p>
      </Modal>
    </div>
  );
}

export default ExamPage;
